<template>
  <v-card
    flat
    class="pa-3 mt-2"
  >
    <v-form
      ref="form"
      v-model="valid"
      @submit.prevent="onSubmit"
    >
      <v-card-text>
        <v-card-title>
          <v-icon
            size="26"
            class="text--primary me-3"
          >
            {{ icons.mdiMapMarker }}
          </v-icon>
          <span>Endereço</span>
        </v-card-title>

        <v-row class="mt-3">
          <v-col
            md="2"
            cols="12"
          >
            <v-text-field
              v-model="cepLocale"
              v-mask="'#####-###'"
              outlined
              dense
              label="CEP"
              :rules="[erro ? validators.cepRequired : validators.required]"
            >
            </v-text-field>
          </v-col>

          <v-col
            md="4"
            cols="12"
          >
            <v-select
              v-model="accountDataLocale.endereco.tipoLogradouro"
              outlined
              dense
              label="Tipo de Logradouro"
              :items="tiposLogradouro"
              :rules="[validators.required]"
            >
            </v-select>
          </v-col>

          <v-col
            md="6"
            sm="8"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.logradouro"
              :hint="`Máximo de 150 caracteres`"
              :maxlength="150"
              outlined
              dense
              label="Nome do Logradouro"
              :rules="[validators.required]"
            ></v-text-field>
          </v-col>

          <v-col
            md="2"
            sm="4"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.numero"
              v-mask="'##########'"
              outlined
              dense
              label="Número"
              :rules="[validators.required]"
            ></v-text-field>
          </v-col>

          <v-col
            md="4"
            sm="6"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.complemento"
              :hint="`Máximo de 20 caracteres`"
              :maxlength="20"
              outlined
              dense
              label="Complemento"
            ></v-text-field>
          </v-col>

          <v-col
            md="6"
            sm="6"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.bairro"
              :hint="`Máximo de 20 caracteres`"
              :maxlength="20"
              outlined
              dense
              label="Bairro"
              :rules="[validators.required]"
            ></v-text-field>
          </v-col>

          <v-col
            md="4"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.cidade"
              :hint="`Máximo de 30 caracteres`"
              :maxlength="30"
              outlined
              dense
              label="Cidade"
              :rules="[validators.required]"
            ></v-text-field>
          </v-col>

          <v-col
            md="2"
            cols="12"
          >
            <v-select
              v-model="accountDataLocale.endereco.estado"
              outlined
              dense
              label="Estado"
              :items="estados"
              :rules="[validators.required]"
            ></v-select>
          </v-col>

          <v-col
            md="6"
            cols="12"
          >
            <v-text-field
              v-model="accountDataLocale.endereco.referencia"
              :hint="`Máximo de 50 caracteres`"
              :maxlength="50"
              outlined
              dense
              label="Referência"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <div class="d-flex justify-space-between mt-2 mx-4">
        <v-btn
          outlined
          color="secondary"
          type="reset"
          @click.prevent="resetForm"
        >
          Voltar
        </v-btn>
        <v-btn
          color="primary"
          type="submit"
        >
          <v-progress-circular
            v-if="loading"
            class="mx-6"
            indeterminate
            color="secundary"
          ></v-progress-circular>
          <span v-else>Finalizar</span>
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { ref, watch, onBeforeMount } from '@vue/composition-api'
import { mdiMapMarker } from '@mdi/js'
import { alphaValidator, emailValidator, passwordValidator, required } from '@core/utils/validation'
import { cepRequired } from '@/utils/validation'
import ColaboradorService from '@/services/modules/ColaboradorService'
import ViaCepService from '@/services/modules/ViaCepService'

export default {
  props: {
    accountData: {
      type: Object,
      default: () => {},
    },
  },

  setup(props, { emit }) {
    const valid = ref(false)
    const form = ref(null)

    const validate = () => {
      form.value.validate()
    }
    const loading = ref(false)

    const estados = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']
    const tiposLogradouro = ['Avenida', 'Rua', 'Praça', 'Alameda', 'Distrito', 'Esplanada', 'Jardim', 'Largo', 'Núcleo', 'Rodovia', 'Via', 'Viaduto', 'Vila', 'Outro']
    const tiposEndereco = ['Residencial', 'Comercial', 'Outro']

    const accountDataLocale = ref(props.accountData)
    const enderecoDefault = {
      bairro: '',
      numero: '',
      tipoLogradouro: '',
      logradouro: '',
      complemento: '',
      referencia: '',
      cidade: '',
      estado: '',
      cep: '',
    }
    watch(() => props.accountData, newValue => {
      accountDataLocale.value = newValue
      accountDataLocale.value.endereco = enderecoDefault
    })

    onBeforeMount(() => {
      if (!accountDataLocale.value.endereco) {
        accountDataLocale.value.endereco = enderecoDefault
      }
    })

    const cepLocale = ref('')
    const erro = ref(false)
    const getCorreio = async () => {
      const data = await ViaCepService.getEnderecoByCep(cepLocale.value)

      erro.value = data.erro

      if (data.cep) {
        const { cep, bairro, logradouro, localidade, uf } = data

        accountDataLocale.value.endereco.cep = cep
        accountDataLocale.value.endereco.bairro = bairro
        accountDataLocale.value.endereco.logradouro = logradouro
        accountDataLocale.value.endereco.cidade = localidade
        accountDataLocale.value.endereco.estado = uf
      }
    }

    watch(cepLocale, newValue => {
      if (newValue.length === 8 || newValue.length === 9) {
        getCorreio()
      }
    })

    const resetForm = () => {
      cepLocale.value = ''
      accountDataLocale.value.endereco = enderecoDefault
      emit('voltarhandleStepInfo')
    }

    const onSubmit = () => {
      loading.value = true

      if (valid.value) {
        ColaboradorService
          .update(accountDataLocale.value)
          .then(() => {
            emit('handleStepInfo')
            emit('handleStepInfos')
            setTimeout(() => {
              loading.value = false
            }, 1000)
          })
      } else {
        loading.value = false
        validate()
      }
    }

    return {
      valid,
      form,
      validate,
      loading,
      estados,
      tiposLogradouro,
      tiposEndereco,
      accountDataLocale,
      erro,
      cepLocale,
      resetForm,
      onSubmit,
      validators: {
        required,
        cepRequired,
        emailValidator,
        passwordValidator,
        alphaValidator,
      },
      icons: {
        mdiMapMarker,
      },
    }
  },
}
</script>
